import React, { useState, useEffect } from "react";
import SavingsDataService from "./services/SavingsService";
import UserDataService from "./services/UserService";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Stack,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  FormControlLabel,
  Switch
} from "@mui/material";
import VerifiedUserRoundedIcon from "@mui/icons-material/VerifiedUserRounded";
import CallSplitRoundedIcon from "@mui/icons-material/CallSplitRounded";
import SmallCalculator from "../components/SmallCalculator";
import CalculateIcon from "@mui/icons-material/Calculate";
import StampDutyService from "./services/StampDutyService";
import SplitDepositModal from "../components/SplitDepositModal";

const toDateInput = (d) => {
  const dt = d ? new Date(d) : new Date();
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const parseNum = (val) => {
  if (val === null || val === undefined || val === "") return 0;
  const clean = String(val).replace(/,/g, "").trim();
  const n = parseFloat(clean);
  return Number.isFinite(n) ? n : 0;
};

const formatInputWithCommas = (val) => {
  if (val === null || val === undefined || val === "") return "";
  const clean = String(val).replace(/[^0-9.]/g, "");
  const parts = clean.split(".");
  const intPart = parts[0] ? Number(parts[0]).toLocaleString("en-US") : "";
  if (parts.length > 1) {
    return `${intPart}.${parts[1].slice(0, 2)}`;
  }
  return intPart;
};

const formatToCurrencyString = (val) => {
  const n = parseNum(val);
  if (n === 0 && (!val || String(val).trim() === "")) return "";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const AddSavings = () => {
  const { owner } = useParams();
  const navigate = useNavigate();
  const initialSavingsState = {
    owner: owner || "",
    date: toDateInput(new Date()),
    amount: "",
    creditOrDebit: "credit",
    description: "",
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null
  };

  const [savings, setSavings] = useState(initialSavingsState);
  const [ownerDisplay, setOwnerDisplay] = useState(() => (UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : ""));
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [error, setError] = useState("");
  const [showCalc, setShowCalc] = useState(false);
  const [applyStampDuty, setApplyStampDuty] = useState(true);
  const [stampDutyAmount, setStampDutyAmount] = useState("50");
  const [waiveReason, setWaiveReason] = useState("");
  const [splitModalOpen, setSplitModalOpen] = useState(false);

  useEffect(() => {
    if (!owner) return;
    if (ownerDisplay) return;
    const cached = UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : "";
    if (cached) {
      setOwnerDisplay(cached);
      return;
    }
    let mounted = true;
    UserDataService.get(owner)
      .then(res => {
        const u = res?.data ?? res ?? {};
        const fname = u.firstName || u.firstname || u.first_name || u.name || "";
        const lname = u.lastName || u.lastname || u.last_name || "";
        const fullName = (lname ? lname + (fname ? " " : "") : "") + fname;
        if (mounted && fullName) setOwnerDisplay(fullName);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [owner, ownerDisplay]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setSavings(prev => ({ ...prev, [name]: value }));
  };

  const saveSavings = async () => {
    setError("");
    const parsedAmount = parseNum(savings.amount);
    if (!savings.amount || parsedAmount <= 0 || Number.isNaN(parsedAmount)) {
      setError("Please enter a valid amount");
      return;
    }
    if (!savings.creditOrDebit) {
      setError("Please select transaction type");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        owner: savings.owner,
        date: typeof savings.date === "string" ? savings.date : toDateInput(savings.date),
        amount: parsedAmount,
        creditOrDebit: String(savings.creditOrDebit),
        description: (savings.description || "").trim()
        // DO NOT send createdAt/createdBy from the client — backend must set createdBy from the authenticated user
      };

      const res = await SavingsDataService.create(savings.owner, payload);
      const r = res?.data || {};

      // Record Stamp Duty or waiver for deposit
      if (savings.creditOrDebit === "credit") {
        const dutyNum = applyStampDuty ? (parseNum(stampDutyAmount) || 50) : 0;
        StampDutyService.record({
          memberId: savings.owner,
          depositAmount: payload.amount,
          stampDutyAmount: dutyNum,
          accountType: "savings",
          isWaived: !applyStampDuty,
          waiveReason: !applyStampDuty ? (waiveReason || "Waived on Savings deposit") : "",
          date: payload.date,
          notes: payload.description || `Savings deposit (Stamp Duty: ₦${dutyNum})`
        }).catch(err => console.error("Stamp duty recording error:", err));
      }

      setSavings({
        owner: r.owner ?? savings.owner,
        date: r.date ?? payload.date,
        amount: r.amount ?? payload.amount,
        creditOrDebit: r.creditOrDebit ?? payload.creditOrDebit,
        description: r.description ?? payload.description,
        createdAt: r.createdAt ?? null,
        createdBy: r.createdBy ?? null,
        updatedAt: r.updatedAt ?? null,
        updatedBy: r.updatedBy ?? null
      });

      // navigate back to the savings list for the owner after successful save
      navigate(`/dashboard/savings/${encodeURIComponent(r.owner ?? savings.owner)}`);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const newSavings = () => {
    setSavings({ ...initialSavingsState, owner: owner || initialSavingsState.owner });
    setSubmitted(false);
    setError("");
    if (!owner) setOwnerDisplay("");
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 840, margin: "24px auto", p: { xs: 2, md: 4 } }}>
      <Typography variant="h6" gutterBottom>
        {submitted ? "Transaction saved" : "Add Savings Transaction"}
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {submitted ? (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" onClick={newSavings}>
              Add Another
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                id="owner"
                name="owner"
                label="Owner"
                value={ownerDisplay || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText={owner ? "Owner provided via route (read-only)" : "Owner (read-only)"}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="date"
                name="date"
                label="Date of Deposit"
                helperText="Date deposit occurred (system creation timestamp is recorded automatically)"
                type="date"
                value={savings.date}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="amount"
                name="amount"
                label="Amount"
                type="text"
                placeholder="e.g. 4,000,000.00"
                value={savings.amount}
                onChange={(e) => {
                  const val = formatInputWithCommas(e.target.value);
                  setSavings(prev => ({ ...prev, amount: val }));
                }}
                onBlur={() => {
                  if (savings.amount) {
                    setSavings(prev => ({ ...prev, amount: formatToCurrencyString(savings.amount) }));
                  }
                }}
                fullWidth
                InputProps={{ inputMode: "numeric" }}
                required
              />
              <Button
                startIcon={<CalculateIcon />}
                size="small"
                sx={{ mt: 1 }}
                onClick={() => setShowCalc(true)}
              >
                Calculator
              </Button>
              <Dialog open={showCalc} keepMounted onClose={() => setShowCalc(false)} maxWidth="xs" fullWidth>
                <DialogContent sx={{ p: 0 }}>
                  <SmallCalculator
                    initialAmount={savings.amount}
                    onApplySavings={(val) => {
                      const v = (val == null) ? "" : formatToCurrencyString(val);
                      setSavings(prev => ({ ...prev, amount: v }));
                      setShowCalc(false);
                    }}
                    onClose={() => setShowCalc(false)}
                  />
                </DialogContent>
              </Dialog>
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2">Transaction Type</Typography>
                <Button
                  variant={savings.creditOrDebit === "credit" ? "contained" : "outlined"}
                  onClick={() => setSavings(s => ({ ...s, creditOrDebit: "credit" }))}
                >
                  Credit
                </Button>
                <Button
                  variant={savings.creditOrDebit === "debit" ? "contained" : "outlined"}
                 onClick={() => setSavings(s => ({ ...s, creditOrDebit: "debit" }))}
                >
                  Debit
                </Button>
              </Stack>
            </Grid>

            {/* STAMP DUTY CONTROL FOR DEPOSIT */}
            {savings.creditOrDebit === "credit" && (
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#F8FAFC",
                    border: "1px solid #E2E8F0"
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <VerifiedUserRoundedIcon sx={{ color: applyStampDuty ? "#0D9488" : "#94A3B8" }} />
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#1E293B" }}>
                        Stamp Duty (EMTL)
                      </Typography>
                    </Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={applyStampDuty}
                          onChange={(e) => setApplyStampDuty(e.target.checked)}
                          color="primary"
                        />
                      }
                      label={applyStampDuty ? `Apply ₦${stampDutyAmount || 50} Stamp Duty` : "Waive Stamp Duty"}
                      sx={{ "& .MuiTypography-root": { fontWeight: 700, fontSize: "0.85rem" } }}
                    />
                  </Box>

                  {applyStampDuty ? (
                    <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: "#475569" }}>
                        Duty Amount:
                      </Typography>
                      {[50, 100, 200].map((preset) => (
                        <Button
                          key={preset}
                          size="small"
                          variant={String(stampDutyAmount) === String(preset) ? "contained" : "outlined"}
                          onClick={() => setStampDutyAmount(String(preset))}
                          sx={{
                            minWidth: 64,
                            py: 0.3,
                            px: 1,
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            borderRadius: "6px",
                            bgcolor: String(stampDutyAmount) === String(preset) ? "#0D9488" : "transparent",
                            color: String(stampDutyAmount) === String(preset) ? "#ffffff" : "#0D9488",
                            borderColor: "#0D9488"
                          }}
                        >
                          ₦{preset} {preset === 50 ? "(Std)" : ""}
                        </Button>
                      ))}
                      <TextField
                        size="small"
                        type="number"
                        label="Custom Duty (₦)"
                        value={stampDutyAmount}
                        onChange={(e) => setStampDutyAmount(e.target.value)}
                        inputProps={{ min: 0 }}
                        sx={{ width: 140, backgroundColor: "#ffffff" }}
                      />
                    </Box>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Reason for waiving (e.g. Single bulk transfer split across savings/shares/loan)"
                      value={waiveReason}
                      onChange={(e) => setWaiveReason(e.target.value)}
                      sx={{ mt: 1.5, backgroundColor: "#ffffff" }}
                    />
                  )}

                  <Box sx={{ mt: 1.5, pt: 1, borderTop: "1px dashed #E2E8F0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
                    <Typography variant="caption" sx={{ color: "#64748B" }}>
                      Is this part of a single bank deposit shared between Savings, Shares, and Loan?
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setSplitModalOpen(true)}
                      startIcon={<CallSplitRoundedIcon />}
                      sx={{ textTransform: "none", fontWeight: 700, borderRadius: "6px", fontSize: "0.75rem", borderColor: "#0D9488", color: "#0D9488" }}
                    >
                      Single Deposit Split Tool (1x Stamp Duty)
                    </Button>
                  </Box>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Description (optional)"
                value={savings.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Enter a brief description of the transaction"
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={saveSavings}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} /> : null}
                >
                  Submit
                </Button>
                <Button variant="outlined" onClick={newSavings} disabled={loading}>
                  Reset
                </Button>
                <Button variant="text" onClick={() => window.history.back()}>
                  Cancel
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: "100%" }}>
          Transaction saved successfully
        </Alert>
      </Snackbar>

      {/* Multi-Account Split Deposit Modal */}
      <SplitDepositModal
        open={splitModalOpen}
        onClose={() => setSplitModalOpen(false)}
        initialMemberId={savings.owner || owner}
        onSuccess={() => {
          navigate(`/dashboard/savings/${encodeURIComponent(savings.owner || owner)}`);
        }}
      />
    </Paper>
  );
};

export default AddSavings;
